import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, resolveBackendAssetUrl } from "../../lib/api";

const POLL_MS = 60 * 1000;
const QR_CARTA_URL = "https://lacasadelpueblo.iaconecta.ai/carta?qr=pantalla";
const QR_CARTA_ASSET = "/screen-assets/qr-carta.png";
const GALLERY_URL = "https://lacasadelpueblo.iaconecta.ai/galeria";
const QR_GALERIA_ASSET = "/screen-assets/qr-galeria.png";

export default function Pantalla() {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const isDebug = params.get("debug") === "1";
  const requestedScreen = params.get("screen") === "social" ? "social" : "info";
  const [playlist, setPlaylist] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [index, setIndex] = useState(0);
  const [qrAssetOk, setQrAssetOk] = useState(true);
  const [galleryQrAssetOk, setGalleryQrAssetOk] = useState(true);
  const [currentGalleryPhoto, setCurrentGalleryPhoto] = useState(null);
  const [currentAlert, setCurrentAlert] = useState(null);
  const galleryPhotoIndexRef = useRef(0);
  const galleryPhotoSignatureRef = useRef("");
  const alertIndexRef = useRef(0);

  const loadScreenData = useCallback(async ({ keepOnError = true } = {}) => {
    const timestamp = Date.now();
    const [playlistResult, photosResult, alertsResult] = await Promise.allSettled([
      api.get("/screen-playlist/active", { params: { screen: requestedScreen, _t: timestamp } }),
      api.get("/visitor-gallery/latest", { params: { limit: 20, _t: timestamp } }),
      api.get("/alerts/active", { params: { _t: timestamp } }),
    ]);

    if (playlistResult.status === "fulfilled") {
      const next = Array.isArray(playlistResult.value.data) ? playlistResult.value.data : [];
      setPlaylist(next);
      setIndex((current) => (next.length ? current % next.length : 0));
    } else if (!keepOnError) {
      setPlaylist([]);
    }

    if (photosResult.status === "fulfilled") {
      const nextPhotos = Array.isArray(photosResult.value.data) ? photosResult.value.data : [];
      const nextSignature = nextPhotos.map((photo) => photo.id).join("|");
      if (nextSignature !== galleryPhotoSignatureRef.current) {
        galleryPhotoSignatureRef.current = nextSignature;
        galleryPhotoIndexRef.current = 0;
      }
      setGalleryPhotos(nextPhotos);
    } else if (!keepOnError) {
      galleryPhotoSignatureRef.current = "";
      galleryPhotoIndexRef.current = 0;
      setGalleryPhotos([]);
    }

    if (alertsResult.status === "fulfilled") {
      setAlerts(Array.isArray(alertsResult.value.data) ? alertsResult.value.data : []);
    } else if (!keepOnError) {
      setAlerts([]);
    }
  }, [requestedScreen]);

  useEffect(() => {
    loadScreenData({ keepOnError: false });
    const id = window.setInterval(() => loadScreenData(), POLL_MS);
    return () => window.clearInterval(id);
  }, [loadScreenData]);

  const activeItems = useMemo(() => {
    return [...playlist].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [playlist]);

  const current = activeItems[index] || null;
  const next = useCallback(() => {
    setIndex((currentIndex) => {
      if (!activeItems.length) return 0;
      return (currentIndex + 1) % activeItems.length;
    });
  }, [activeItems.length]);

  const previous = useCallback(() => {
    setIndex((currentIndex) => {
      if (!activeItems.length) return 0;
      return (currentIndex - 1 + activeItems.length) % activeItems.length;
    });
  }, [activeItems.length]);

  useEffect(() => {
    if (!current) {
      setCurrentGalleryPhoto(null);
      setCurrentAlert(null);
      return undefined;
    }

    if (current.type === "gallery_photo") {
      setCurrentAlert(null);
      if (!galleryPhotos.length) {
        setCurrentGalleryPhoto(null);
        const id = window.setTimeout(next, 0);
        return () => window.clearTimeout(id);
      }
      const photo = galleryPhotos[galleryPhotoIndexRef.current % galleryPhotos.length];
      galleryPhotoIndexRef.current += 1;
      setCurrentGalleryPhoto(photo);
      return undefined;
    }

    if (current.type === "alert") {
      setCurrentGalleryPhoto(null);
      if (!alerts.length) {
        setCurrentAlert(null);
        const id = window.setTimeout(next, 0);
        return () => window.clearTimeout(id);
      }
      const alert = alerts[alertIndexRef.current % alerts.length];
      alertIndexRef.current += 1;
      setCurrentAlert(alert);
      return undefined;
    }

    setCurrentGalleryPhoto(null);
    setCurrentAlert(null);
    return undefined;
  }, [alerts, current, galleryPhotos, next]);

  useEffect(() => {
    if (!current || current.type === "video") return undefined;
    if (current.type === "gallery_photo" && !currentGalleryPhoto) return undefined;
    if (current.type === "alert" && !currentAlert) return undefined;
    const seconds = Number(current.duration) || 10;
    const id = window.setTimeout(next, seconds * 1000);
    return () => window.clearTimeout(id);
  }, [current, currentAlert, currentGalleryPhoto, next]);

  useEffect(() => {
    if (activeItems.length < 2) return;
    const upcoming = activeItems[(index + 1) % activeItems.length];
    if (!upcoming?.file_url) return;
    const url = resolveBackendAssetUrl(upcoming.file_url);
    if (!url) return;
    if (upcoming.type === "image") {
      const img = new Image();
      img.src = url;
    }
    if (upcoming.type === "video") {
      const video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.src = url;
      video.load();
    }
  }, [activeItems, index]);

  return (
    <main className="fixed inset-0 h-screen w-screen overflow-hidden bg-black text-white" data-testid="pantalla-page">
      <div className="relative h-full w-full">
        {!current ? (
          <FallbackSlide />
        ) : current.type === "qr_carta" ? (
          <QrCartaSlide item={current} assetOk={qrAssetOk} onAssetError={() => setQrAssetOk(false)} />
        ) : current.type === "video" ? (
          <VideoSlide item={current} onDone={next} />
        ) : current.type === "gallery_photo" ? (
          <GalleryPhotoSlide photo={currentGalleryPhoto} />
        ) : current.type === "alert" ? (
          <AlertSlide alert={currentAlert} />
        ) : current.type === "gallery_cta" ? (
          <GalleryCtaSlide assetOk={galleryQrAssetOk} onAssetError={() => setGalleryQrAssetOk(false)} />
        ) : (
          <ImageSlide item={current} />
        )}
        {isDebug && (
          <DebugControls onPrevious={previous} onNext={next} onReload={() => loadScreenData()} />
        )}
      </div>
    </main>
  );
}

function FallbackSlide() {
  return (
    <section className="grid h-full w-full place-items-center bg-neutral-950 px-[8vw] text-center">
      <div>
        <div className="font-display text-[8vw] font-black leading-none tracking-normal">Casa del Pueblo</div>
        <div className="mt-[2vh] text-[2.6vw] font-bold uppercase tracking-[0.25em] text-white/70">
          Feria de Albacete
        </div>
      </div>
    </section>
  );
}

function QrCartaSlide({ item, assetOk, onAssetError }) {
  return (
    <section className="grid h-full w-full place-items-center bg-neutral-950 px-[7vw] py-[7vh]">
      <div className="grid w-full max-w-[1500px] grid-cols-[1fr_0.72fr] items-center gap-[5vw]">
        <div>
          <div className="text-[2vw] font-bold uppercase tracking-[0.25em] text-primary">Carta</div>
          <h1 className="mt-[2vh] font-display text-[7vw] font-black leading-none tracking-normal">
            Escanea y consulta la carta
          </h1>
          <p className="mt-[4vh] text-[2.2vw] leading-tight text-white/75">
            Platos, bebidas y precios actualizados durante la Feria.
          </p>
        </div>
        <div className="grid place-items-center rounded-[2vw] bg-white p-[2vw] shadow-2xl">
          {assetOk ? (
            <img
              src={QR_CARTA_ASSET}
              alt={item.title || "QR Carta"}
              className="aspect-square w-full object-contain"
              onError={onAssetError}
            />
          ) : (
            <div className="grid aspect-square w-full place-items-center p-[2vw] text-center text-neutral-950">
              <div>
                <div className="font-display text-[3.2vw] font-black">QR Carta</div>
                <div className="mt-[2vh] break-all text-[1.3vw] font-bold">{QR_CARTA_URL}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ImageSlide({ item }) {
  const url = resolveBackendAssetUrl(item.file_url);
  const fitClass = item.fit === "cover" ? "object-cover" : "object-contain";
  return (
    <section className="h-full w-full bg-black">
      {url ? (
        <img src={url} alt={item.title} className={`h-full w-full ${fitClass}`} />
      ) : (
        <FallbackSlide />
      )}
    </section>
  );
}

function GalleryPhotoSlide({ photo }) {
  const url = resolveBackendAssetUrl(photo?.url);
  if (!url) return <FallbackSlide />;
  return (
    <section className="h-full w-full bg-black">
      <img
        src={url}
        alt={photo.comment || "Foto de visitantes"}
        className="h-full w-full object-contain object-center"
      />
    </section>
  );
}

function DebugControls({ onPrevious, onNext, onReload }) {
  return (
    <div className="absolute bottom-4 right-4 z-50 flex gap-2 rounded-lg bg-black/45 p-2 text-xs font-bold text-white backdrop-blur">
      <button
        type="button"
        onClick={onPrevious}
        className="rounded-md bg-white/15 px-3 py-2 hover:bg-white/25"
      >
        Anterior
      </button>
      <button
        type="button"
        onClick={onNext}
        className="rounded-md bg-white/15 px-3 py-2 hover:bg-white/25"
      >
        Siguiente
      </button>
      <button
        type="button"
        onClick={onReload}
        className="rounded-md bg-white/15 px-3 py-2 hover:bg-white/25"
      >
        Recargar datos
      </button>
    </div>
  );
}

function AlertSlide({ alert }) {
  if (!alert) return <FallbackSlide />;
  const imageUrl = resolveBackendAssetUrl(alert.image_url);
  if (imageUrl) {
    return (
      <section className="grid h-full w-full grid-cols-[1.05fr_0.95fr] bg-neutral-950 text-white">
        <div className="relative h-full w-full overflow-hidden">
          <img src={imageUrl} alt={alert.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-black/60" />
        </div>
        <div className="flex h-full flex-col justify-center px-[5vw] py-[8vh]">
          <div className="text-[2vw] font-bold uppercase tracking-[0.25em] text-primary">Aviso</div>
          <h1 className="mt-[3vh] font-display text-[6vw] font-black leading-none tracking-normal">
            {alert.title}
          </h1>
          <p className="mt-[4vh] text-[2.8vw] font-semibold leading-tight text-white/82">
            {alert.message}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="grid h-full w-full place-items-center bg-neutral-950 px-[8vw] py-[8vh] text-center text-white">
      <div className="max-w-[1500px]">
        <div className="text-[2vw] font-bold uppercase tracking-[0.25em] text-primary">Aviso</div>
        <h1 className="mt-[3vh] font-display text-[7vw] font-black leading-none tracking-normal">
          {alert.title}
        </h1>
        <p className="mt-[5vh] text-[3.2vw] font-semibold leading-tight text-white/80">
          {alert.message}
        </p>
      </div>
    </section>
  );
}

function GalleryCtaSlide({ assetOk, onAssetError }) {
  return (
    <section className="grid h-full w-full place-items-center bg-neutral-950 px-[7vw] py-[7vh]">
      <div className="grid w-full max-w-[1500px] grid-cols-[1fr_0.72fr] items-center gap-[5vw]">
        <div>
          <div className="text-[2vw] font-bold uppercase tracking-[0.25em] text-primary">Galeria</div>
          <h1 className="mt-[2vh] font-display text-[6.6vw] font-black leading-none tracking-normal">
            Quieres salir en nuestras pantallas?
          </h1>
          <p className="mt-[4vh] text-[2.2vw] leading-tight text-white/75">
            Escanea el QR y sube tu foto. Tras aprobarla, podra aparecer aqui.
          </p>
        </div>
        <div className="grid place-items-center rounded-[2vw] bg-white p-[2vw] shadow-2xl">
          {assetOk ? (
            <img
              src={QR_GALERIA_ASSET}
              alt="QR Galeria"
              className="aspect-square w-full object-contain"
              onError={onAssetError}
            />
          ) : (
            <div className="grid aspect-square w-full place-items-center p-[2vw] text-center text-neutral-950">
              <div>
                <div className="font-display text-[3.2vw] font-black">QR Galeria</div>
                <div className="mt-[2vh] break-all text-[1.3vw] font-bold">{GALLERY_URL}</div>
                <div className="mt-[2vh] text-[1vw] font-semibold text-neutral-600">
                  Falta /screen-assets/qr-galeria.png
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function VideoSlide({ item, onDone }) {
  const fallbackTimer = useRef(null);
  const url = resolveBackendAssetUrl(item.file_url);
  const fitClass = item.fit === "cover" ? "object-cover" : "object-contain";
  useEffect(() => {
    return () => {
      if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
    };
  }, []);
  const handleError = () => {
    if (fallbackTimer.current) return;
    const seconds = Number(item.duration) || 10;
    fallbackTimer.current = window.setTimeout(onDone, seconds * 1000);
  };
  if (!url) return <FallbackSlide />;
  return (
    <section className="h-full w-full bg-black">
      <video
        key={item.id}
        src={url}
        className={`h-full w-full ${fitClass}`}
        autoPlay
        muted
        playsInline
        controls={false}
        preload="auto"
        onEnded={onDone}
        onError={handleError}
      />
    </section>
  );
}
